import { useMemo, useState } from 'react';

import type {
  ChartOfAccount,
  CreateApCreditDebitNote,
  CreateApCreditDebitNoteLine,
  CreditDebitType,
} from '@dcc-erp/shared';
import { AP_CREDIT_DEBIT_NOTE_REASONS, dateToInputValue, fmt, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { useActiveCompanyId, useCoa } from '../../lib/queries';
import { useCreateApCreditDebitNote, useSubmitApNote } from '../../lib/queries/ap';
import { AppModal } from '../AppModal';
import { modalBody, modalFooter, modalLabel } from '../ar/modal-style';

import type { CSSProperties } from 'react';

// =============================================================================
// Create an AP Credit/Debit Note (ใบลดหนี้ / ใบเพิ่มหนี้) from a POSTED bill.
//
// CreateCreditDebitNoteModal (container) owns the COA fetch + the two-step
// create-then-submit engine flow; the inner CreateCreditDebitNoteForm
// (presentational, router/data-free) owns the noteType toggle + reason select +
// line grid + totals bar so the submit payload is unit-testable without a query
// client. Mirrors the AR CreateCreditNoteModal split, flipping revenueCode →
// expenseCode and adding the noteType (CREDIT/DEBIT) discriminator + a CREDIT
// over-cap warning (a CREDIT can't exceed the bill's current outstanding).
// Plain prototype.css classes / inline styles — no tailwind.
// =============================================================================

const reasonOptions = Object.entries(AP_CREDIT_DEBIT_NOTE_REASONS).map(([value, label]) => ({
  value,
  label,
}));

// modalError differs from the shared ar/modal-style version (no marginTop) so
// it stays local; the other 7 chrome consts are imported verbatim.
const modalError: CSSProperties = {
  color: 'var(--negative)',
  fontSize: 14,
};

// The form only needs the bill's id + vatRate + outstanding + (optionally) the
// first expense code to seed a line. Accept a lean subset so callers can pass
// either a list row or the full detail.
export interface CreditDebitNoteBill {
  id: string;
  voucherNo: string;
  vatRate: string | number;
  /** Note-adjusted outstanding (total + noteAdjustment − paid). The form caps a
   *  CREDIT to this. */
  outstanding: number;
  /** First expense code on the bill (used to seed the starting line). */
  defaultExpenseCode?: string;
}

type ExpenseAccount = Pick<ChartOfAccount, 'code' | 'nameTh'>;

// Wire line + a client-only stable key (`_uid`). Stripped before submit.
type LineRow = CreateApCreditDebitNoteLine & { _uid: string };

// The form emits this wire shape; the container parses it into CreateApCreditDebitNote.
export interface CreateApCreditDebitNotePayload {
  noteType: CreditDebitType;
  noteDate: string;
  billId: string;
  reasonCode: string;
  reasonNote?: string;
  vatRate: number;
  lines: CreateApCreditDebitNoteLine[];
}

export interface CreateCreditDebitNoteFormProps {
  bill: CreditDebitNoteBill;
  expenseAccounts: ExpenseAccount[];
  onSubmit: (payload: CreateApCreditDebitNotePayload) => void;
  submitting: boolean;
}

export function CreateCreditDebitNoteForm({
  bill,
  expenseAccounts,
  onSubmit,
  submitting,
}: CreateCreditDebitNoteFormProps) {
  const seedExpenseCode = bill.defaultExpenseCode ?? expenseAccounts[0]?.code ?? '';

  // Each line carries a stable synthetic `_uid` used only as the React list key,
  // so removing a middle row doesn't reassign a row's identity to its neighbour
  // (index keys would). `_uid` is never part of the wire payload — cleanLines
  // strips it back to the CreateApCreditDebitNoteLine shape before submit.
  const [noteType, setNoteType] = useState<CreditDebitType>('CREDIT');
  const [noteDate, setNoteDate] = useState(dateToInputValue(new Date()));
  const [reasonCode, setReasonCode] = useState<string>(reasonOptions[0]?.value ?? '');
  const [reasonNote, setReasonNote] = useState('');
  const [vatRate, setVatRate] = useState<number>(toNumber(bill.vatRate));
  const [lines, setLines] = useState<LineRow[]>(() => [
    {
      _uid: crypto.randomUUID(),
      description: '',
      qty: 1,
      unitPrice: 0,
      expenseCode: seedExpenseCode,
    },
  ]);

  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unitPrice || 0), 0),
    [lines],
  );
  const vatAmount = useMemo(() => (subtotal * vatRate) / 100, [subtotal, vatRate]);
  const totalAmount = subtotal + vatAmount;

  // A CREDIT note can't exceed the bill's current outstanding (no negative
  // payable). DEBIT has no cap. The server enforces this; the warning is a
  // pre-submit hint (the submit button is also blocked while over cap).
  const overCap = noteType === 'CREDIT' && totalAmount > bill.outstanding + 0.005;

  function updateLine(idx: number, patch: Partial<CreateApCreditDebitNoteLine>) {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((prev) => [
      ...prev,
      {
        _uid: crypto.randomUUID(),
        description: '',
        qty: 1,
        unitPrice: 0,
        expenseCode: seedExpenseCode,
      },
    ]);
  }
  function removeLine(idx: number) {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  }

  // Strip the client-only `_uid` back to the wire shape before submit.
  const cleanLines = useMemo<CreateApCreditDebitNoteLine[]>(
    () =>
      lines
        .filter((l) => l.description && Number(l.qty) > 0 && Number(l.unitPrice) >= 0)
        .map((l) => ({
          description: l.description,
          qty: l.qty,
          unitPrice: l.unitPrice,
          expenseCode: l.expenseCode,
        })),
    [lines],
  );

  const disabled =
    !reasonCode || !noteDate || cleanLines.length === 0 || subtotal <= 0 || overCap || submitting;

  function handleSubmit() {
    onSubmit({
      noteType,
      noteDate,
      billId: bill.id,
      reasonCode,
      ...(reasonNote ? { reasonNote } : {}),
      vatRate,
      lines: cleanLines,
    });
  }

  const isCredit = noteType === 'CREDIT';

  return (
    <div style={modalBody}>
      {/* noteType toggle — ใบลดหนี้ (CREDIT) / ใบเพิ่มหนี้ (DEBIT) */}
      <div style={{ display: 'flex', gap: 8 }}>
        {(
          [
            ['CREDIT', 'ใบลดหนี้', 'var(--positive)'],
            ['DEBIT', 'ใบเพิ่มหนี้', 'var(--warning)'],
          ] as const
        ).map(([value, label, color]) => (
          <button
            key={value}
            type="button"
            aria-label={`note-type-${value}`}
            aria-pressed={noteType === value}
            onClick={() => setNoteType(value)}
            style={{
              flex: 1,
              padding: '10px 12px',
              borderRadius: 8,
              cursor: 'pointer',
              border: noteType === value ? `2px solid ${color}` : '1px solid var(--border)',
              background: noteType === value ? `${color}15` : 'white',
              fontWeight: 600,
            }}
          >
            {noteType === value ? '● ' : ''}
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px', gap: 12 }}>
        <div>
          <label style={modalLabel}>วันที่ออก{isCredit ? 'ใบลดหนี้' : 'ใบเพิ่มหนี้'}</label>
          <input
            type="date"
            className="input"
            aria-label="note-date"
            value={noteDate}
            onChange={(e) => setNoteDate(e.target.value)}
            style={{ width: '100%', height: 38, fontSize: 14 }}
          />
        </div>
        <div>
          <label style={modalLabel}>เหตุผล</label>
          <select
            className="select"
            aria-label="note-reason"
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
            style={{ width: '100%', height: 38, fontSize: 14 }}
          >
            {reasonOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={modalLabel}>VAT (%)</label>
          <input
            type="number"
            className="input tnum"
            aria-label="note-vat-rate"
            value={vatRate}
            onChange={(e) => setVatRate(Number(e.target.value))}
            min={0}
            max={100}
            step={0.01}
            style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
          />
        </div>
      </div>

      <div>
        <label style={modalLabel}>หมายเหตุ (optional)</label>
        <input
          className="input"
          aria-label="note-reason-note"
          value={reasonNote}
          onChange={(e) => setReasonNote(e.target.value)}
          placeholder="รายละเอียดเพิ่มเติม"
          style={{ width: '100%', height: 38, fontSize: 14 }}
        />
      </div>

      <div>
        <label style={modalLabel}>รายการ</label>
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>คำอธิบาย</th>
                <th className="num" style={{ width: 80 }}>
                  จำนวน
                </th>
                <th className="num" style={{ width: 110 }}>
                  ราคา/หน่วย
                </th>
                <th style={{ width: 160 }}>บัญชีค่าใช้จ่าย</th>
                <th className="num" style={{ width: 110 }}>
                  ยอดเงิน
                </th>
                <th style={{ width: 30 }} />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => {
                const amount = Number(l.qty || 0) * Number(l.unitPrice || 0);
                return (
                  <tr key={l._uid}>
                    <td>
                      <input
                        className="input"
                        aria-label={`note-line-desc-${idx}`}
                        value={l.description}
                        onChange={(e) => updateLine(idx, { description: e.target.value })}
                        style={{ width: '100%', height: 32, fontSize: 13 }}
                      />
                    </td>
                    <td className="num">
                      <input
                        type="number"
                        className="input tnum"
                        aria-label={`note-line-qty-${idx}`}
                        value={l.qty}
                        min={0}
                        step={0.0001}
                        onChange={(e) => updateLine(idx, { qty: Number(e.target.value) })}
                        style={{ width: '100%', height: 32, fontSize: 13, textAlign: 'right' }}
                      />
                    </td>
                    <td className="num">
                      <input
                        type="number"
                        className="input tnum"
                        aria-label={`note-line-price-${idx}`}
                        value={l.unitPrice}
                        min={0}
                        step={0.01}
                        onChange={(e) => updateLine(idx, { unitPrice: Number(e.target.value) })}
                        style={{ width: '100%', height: 32, fontSize: 13, textAlign: 'right' }}
                      />
                    </td>
                    <td>
                      <select
                        className="select"
                        aria-label={`note-line-expense-${idx}`}
                        value={l.expenseCode}
                        onChange={(e) => updateLine(idx, { expenseCode: e.target.value })}
                        style={{ width: '100%', height: 32, fontSize: 13 }}
                      >
                        {expenseAccounts.length === 0 && (
                          <option value={l.expenseCode}>{l.expenseCode}</option>
                        )}
                        {expenseAccounts.map((a) => (
                          <option key={a.code} value={a.code}>
                            {a.code} · {a.nameTh}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="num tnum">{fmt(amount, 2)}</td>
                    <td>
                      {lines.length > 1 && (
                        <button
                          className="btn ghost"
                          onClick={() => removeLine(idx)}
                          style={{ padding: '0 6px' }}
                        >
                          <Icon name="x" size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <button className="btn ghost" onClick={addLine} style={{ marginTop: 8, fontSize: 13 }}>
          <Icon name="plus" size={12} /> เพิ่มรายการ
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          rowGap: 4,
          fontSize: 14,
          marginTop: 8,
        }}
      >
        <div className="muted">ยอดก่อน VAT</div>
        <div className="tnum" style={{ textAlign: 'right' }}>
          {fmt(subtotal, 2)}
        </div>
        <div className="muted">VAT ({vatRate}%)</div>
        <div className="tnum" style={{ textAlign: 'right' }}>
          {fmt(vatAmount, 2)}
        </div>
        <div style={{ fontWeight: 600 }}>รวมทั้งสิ้น</div>
        <div className="tnum" style={{ textAlign: 'right', fontWeight: 600 }}>
          {fmt(totalAmount, 2)}
        </div>
      </div>

      {/* CREDIT over-cap warning — total exceeds the bill's outstanding. */}
      {overCap && (
        <div
          data-testid="note-cap-warning"
          style={{
            background: 'var(--warning-bg, #fffbeb)',
            border: '1px solid var(--warning)',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 13,
          }}
        >
          <strong style={{ color: 'var(--warning)' }}>ยอดเกินคงค้าง:</strong> ใบลดหนี้รวม{' '}
          {fmt(totalAmount, 2)} เกินยอดคงค้างของบิล ({fmt(bill.outstanding, 2)}) — ปรับยอดให้ไม่เกิน
        </div>
      )}

      <div style={modalFooter}>
        <button type="button" className="btn primary" disabled={disabled} onClick={handleSubmit}>
          <Icon name="check" size={14} />{' '}
          {submitting ? 'กำลังบันทึก…' : `ออก${isCredit ? 'ใบลดหนี้' : 'ใบเพิ่มหนี้'}`}
        </button>
      </div>
    </div>
  );
}

export interface CreateCreditDebitNoteModalProps {
  bill: CreditDebitNoteBill;
  onClose: () => void;
}

export function CreateCreditDebitNoteModal({ bill, onClose }: CreateCreditDebitNoteModalProps) {
  const companyId = useActiveCompanyId();
  const create = useCreateApCreditDebitNote(companyId);
  const submit = useSubmitApNote(companyId);

  // EXPENSE leaf accounts only — control/header accounts can't be posted to.
  const { data: coa = [] } = useCoa(companyId, true);
  const expenseAccounts = useMemo(
    () => coa.filter((c) => c.type === 'EXPENSE' && c.isActive && !c.isControl),
    [coa],
  );

  const error = create.error ?? submit.error;
  const pending = create.isPending || submit.isPending;

  async function handleSubmit(payload: CreateApCreditDebitNotePayload) {
    // Two-step engine flow: create the DRAFT note, then submit it into the
    // approval engine (the note JV books on final approval, not here).
    const data: CreateApCreditDebitNote = {
      noteType: payload.noteType,
      noteDate: payload.noteDate,
      billId: payload.billId,
      reasonCode: payload.reasonCode,
      ...(payload.reasonNote ? { reasonNote: payload.reasonNote } : {}),
      vatRate: payload.vatRate,
      lines: payload.lines,
    };
    const created = await create.mutateAsync({ data });
    await submit.mutateAsync(created.id);
    onClose();
  }

  // WS8 R2 — migrated off the bespoke 760px chrome to the standard AppModal
  // (1200px). Container-only: the presentational form keeps its body + in-body
  // submit button (no test renders this container's chrome).
  return (
    <AppModal title="ออกใบลดหนี้ / ใบเพิ่มหนี้" onClose={onClose}>
      <CreateCreditDebitNoteForm
        bill={bill}
        expenseAccounts={expenseAccounts}
        onSubmit={(payload) => void handleSubmit(payload)}
        submitting={pending}
      />
      {error && (
        <div style={{ ...modalError, padding: '0 22px 16px' }}>
          บันทึกไม่สำเร็จ: {error.message}
        </div>
      )}
    </AppModal>
  );
}
